/**
 * One place that decides whether a discovered incident belongs to the
 * operational context the operator is currently standing in.
 *
 * Discovery already answered a different question — *may* this account read the
 * incident. That answer is authoritative and is never re-derived here. What it
 * cannot answer is whether the incident belongs to the work context the
 * operator selected in the shell, because one account routinely holds several:
 * an OV duty role, a Regionalstelle coordination role, an appointment in a
 * neighbouring OV's incident. Offering all of them at once is how an operator
 * ends up commanding the wrong incident.
 *
 * The server states the reason each incident is visible
 * (`operationalRelation`), which is what makes this decidable at all: an
 * organization-derived relation narrows with the selected organization, while a
 * relation granted to the person or their unit must survive that narrowing.
 */

/**
 * Why the server let this account see the incident. Mirrors
 * `IncidentOperationalRelation` in `m08-incident/application/incident-authorization.ts`.
 */
export type IncidentOperationalRelation =
  | "APPOINTED"
  | "OWNED"
  | "PARTICIPATING"
  | "ADMINISTRATIVE";

/** The narrowed work context a projection is being judged against. */
export interface OperationalScopeBinding {
  readonly roleCode: string | null;
  /**
   * Already passed through the cross-organization role exemption, so `null`
   * means "this context is not organization-narrowed" rather than "unknown".
   */
  readonly organizationUnitId: string | null;
}

export interface ScopeJudgeableIncident {
  readonly incidentKind: "LOCAL_LUK" | "REGIONAL_PARENT";
  readonly owningOrganizationUnitId: string;
  /**
   * Optional only so fixtures and any server that predates the attribute stay
   * loadable. Absent is treated as organization-derived — the narrowing case —
   * so a missing attribute can never widen what an operator is offered.
   */
  readonly operationalRelation?: IncidentOperationalRelation;
}

/**
 * Relations granted to the person or their unit rather than to the organization
 * they are currently working under. These are precisely the handoff paths an
 * owner-only filter would destroy, so they are exempt from organization
 * narrowing.
 */
const CONTEXT_INDEPENDENT_RELATIONS: ReadonlySet<IncidentOperationalRelation> =
  new Set(["APPOINTED", "PARTICIPATING"]);

/**
 * Access personas that address exactly one kind of operational record. Mirrors
 * `LOCAL_LUK_ROLE_CODES` and the `regionalstelle` branch in the API's
 * `ownedIncidentPermissionAllowed`, which refuses the same combinations.
 */
const LOCAL_LUK_ROLE_CODES: ReadonlySet<string> = new Set([
  "luk_dispatcher",
  "luk_operator",
]);

export function incidentBelongsToOperationalScope(
  incident: ScopeJudgeableIncident,
  scope: OperationalScopeBinding,
): boolean {
  if (CONTEXT_INDEPENDENT_RELATIONS.has(incident.operationalRelation ?? "OWNED")) {
    return true;
  }
  // A context with no organization is a cross-organization administration or
  // reference perspective; discovery's own answer already is the boundary.
  if (scope.organizationUnitId === null) return true;
  if (incident.owningOrganizationUnitId !== scope.organizationUnitId) return false;
  if (scope.roleCode === "regionalstelle") {
    return incident.incidentKind === "REGIONAL_PARENT";
  }
  if (scope.roleCode !== null && LOCAL_LUK_ROLE_CODES.has(scope.roleCode)) {
    return incident.incidentKind === "LOCAL_LUK";
  }
  return true;
}
